import {
  DAILY_DEPARTMENTS,
  DAILY_REPORT_KINDS,
  DAILY_SUMMARY_MAX,
  type DailyReportKind,
  parseDailyReport,
} from '@felix/domain';
import {
  Button,
  FileButton,
  Select,
  Switch,
  Textarea,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import {
  IconCloudUpload,
  IconDeviceFloppy,
  IconFile,
  IconRefresh,
  IconSend,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTimeInput } from '../../inputs/DateTimeInput.js';
import { DayInput } from '../../inputs/DayInput.js';
import classes from './Daily.module.css';
import { DAILY_SECTIONS, type FieldSpec, fieldLabelKey, REPORT_FIELDS } from './logic.js';
import { localValueOf, useErrorText, utcOfLocal } from './parts.js';
import type { DailyEditor, DailyReportDetail, DailyReportInput } from './types.js';

export interface ReportDraft {
  kind: DailyReportKind;
  /** Gemi saatiyle `YYYY-MM-DDTHH:mm`. */
  local: string;
  department: string | null;
  summary: string;
  data: Record<string, unknown>;
}

export function draftOf(
  detail: DailyReportDetail | null,
  kind: DailyReportKind,
  nowUtc: string,
  offset: number,
): ReportDraft {
  if (detail) {
    return {
      kind: detail.kind,
      local: localValueOf(detail.reportedAt, detail.tzOffsetMin),
      department: detail.department,
      summary: detail.summary,
      data: { ...detail.data },
    };
  }
  return { kind, local: localValueOf(nowUtc, offset), department: null, summary: '', data: {} };
}

export function inputOf(draft: ReportDraft, offset: number): DailyReportInput {
  return {
    kind: draft.kind,
    reportedAt: utcOfLocal(draft.local, offset),
    department: draft.department,
    summary: draft.summary,
    data: draft.data,
  };
}

/**
 * BGM "Daily Report Entry": Daily'de General / Deck / Engine / Safety / Environment / Remarks
 * sekmeleri, ötekilerde türün alanları; sağda ekler. Tarih gemi saatiyle girilir, gün ondan türer
 * (ADR 0051 §5). Taslak kaydedilir; gönderim ayrı (zorunlular gönderimde).
 */
export function ReportForm({
  detail,
  kind,
  vesselName,
  tzOffsetMin,
  nowUtc,
  editor,
  onSaved,
  onSubmit,
  onCancel,
}: {
  detail: DailyReportDetail | null;
  kind: DailyReportKind;
  vesselName: string;
  tzOffsetMin: number;
  nowUtc: string;
  editor: DailyEditor;
  onSaved: (id: string) => void;
  onSubmit: (id: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`daily.form.${key}`, options ?? {});
  const errorText = useErrorText();
  const offset = detail?.tzOffsetMin ?? tzOffsetMin;
  const initial = useMemo(
    () => draftOf(detail, kind, nowUtc, offset),
    [detail, kind, nowUtc, offset],
  );
  const [draft, setDraft] = useState<ReportDraft>(initial);
  const [tab, setTab] = useState<'general' | (typeof DAILY_SECTIONS)[number]>('general');
  const [busy, setBusy] = useState<'save' | 'submit' | 'upload' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<string[]>([]);
  const setData = (key: string, value: unknown) =>
    setDraft((d) => ({ ...d, data: { ...d.data, [key]: value === '' ? null : value } }));

  const save = async (): Promise<string> => {
    const input = inputOf(draft, offset);
    const checked = parseDailyReport({ ...input, tzOffsetMin: offset }, 'draft');
    if (!checked.ok) {
      setInvalid(checked.issues.map((i) => i.path));
      throw Object.assign(new Error('INVALID_DAILY_REPORT'), { code: 'INVALID_DAILY_REPORT' });
    }
    if (detail) {
      const { kind: _kind, ...rest } = input;
      await editor.updateReport(detail.id, detail.rowVersion, rest);
      return detail.id;
    }
    return editor.openReport(input);
  };
  const act = (kindOf: 'save' | 'submit') => {
    setBusy(kindOf);
    setError(null);
    setInvalid([]);
    save()
      .then((id) => (kindOf === 'save' ? onSaved(id) : onSubmit(id)))
      .catch((failure: unknown) => setError(errorText(failure)))
      .finally(() => setBusy(null));
  };
  const missing = (path: string) => (invalid.includes(path) ? k('required') : undefined);

  const field = (spec: FieldSpec) => {
    const label = k(fieldLabelKey(spec.key));
    const value = draft.data[spec.key];
    const err = missing(`data.${spec.key}`);
    switch (spec.type) {
      case 'select':
        return (
          <Select
            key={spec.key}
            label={label}
            data={spec.options.map((o) => ({ value: o, label: t(`daily.${spec.labels}.${o}`) }))}
            value={typeof value === 'string' ? value : null}
            onChange={(v) => setData(spec.key, v)}
            withAsterisk={spec.required === true}
            error={err}
            clearable
          />
        );
      case 'day':
        return (
          <DayInput
            key={spec.key}
            label={label}
            value={typeof value === 'string' ? value : null}
            onChange={(v) => setData(spec.key, v)}
          />
        );
      case 'switch':
        return (
          <Switch
            key={spec.key}
            className={classes.switchField}
            label={label}
            checked={value === true}
            onChange={(event) => setData(spec.key, event.currentTarget.checked)}
          />
        );
      case 'textarea':
        return (
          <Textarea
            key={spec.key}
            label={label}
            className={classes.wide}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => setData(spec.key, event.currentTarget.value)}
            autosize
            minRows={2}
            maxLength={2000}
            error={err}
          />
        );
      default:
        return (
          <TextInput
            key={spec.key}
            label={label}
            {...(spec.key === 'position' ? { placeholder: k('positionPlaceholder') } : {})}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => setData(spec.key, event.currentTarget.value)}
            maxLength={200}
            withAsterisk={spec.required === true}
            error={err}
          />
        );
    }
  };

  const tabs =
    draft.kind === 'daily' ? (['general', ...DAILY_SECTIONS] as const) : (['general'] as const);
  return (
    <section className={classes.card} aria-labelledby="daily-form-title">
      <h2 id="daily-form-title" className={classes.cardTitle}>
        {detail ? k('editTitle', { kind: t(`daily.kinds.${detail.kind}`) }) : k('title')}
      </h2>
      {tabs.length > 1 ? (
        <div className={classes.tabs} role="tablist" aria-label={k('title')}>
          {tabs.map((name) => (
            <UnstyledButton
              key={name}
              role="tab"
              aria-selected={tab === name}
              className={classes.tab}
              data-active={tab === name || undefined}
              onClick={() => setTab(name)}
            >
              {k(`tabs.${name}`)}
            </UnstyledButton>
          ))}
        </div>
      ) : null}
      <form
        className={classes.entry}
        onSubmit={(event) => {
          event.preventDefault();
          act('save');
        }}
      >
        <div className={classes.entryMain}>
          {tab === 'general' ? (
            <>
              <div className={classes.formGrid} data-columns="3">
                <DateTimeInput
                  label={k('reportDate')}
                  description={k('shipTime')}
                  value={draft.local}
                  onChange={(v) => v && setDraft((d) => ({ ...d, local: v }))}
                  maxDate={localValueOf(nowUtc, offset)}
                  required
                />
                <TextInput label={k('vessel')} value={vesselName} readOnly />
                <Select
                  label={k('reportType')}
                  data={DAILY_REPORT_KINDS.map((v) => ({ value: v, label: t(`daily.kinds.${v}`) }))}
                  value={draft.kind}
                  onChange={(v) =>
                    v && setDraft((d) => ({ ...d, kind: v as DailyReportKind, data: {} }))
                  }
                  disabled={detail !== null}
                  allowDeselect={false}
                  required
                />
                <Select
                  label={k('department')}
                  placeholder={k('departmentAll')}
                  data={DAILY_DEPARTMENTS.filter((d) => d !== 'all').map((d) => ({
                    value: d,
                    label: t(`daily.departments.${d}`),
                  }))}
                  value={draft.department}
                  onChange={(v) => setDraft((d) => ({ ...d, department: v }))}
                  clearable
                />
                {REPORT_FIELDS[draft.kind].map(field)}
              </div>
              <Textarea
                label={k('summary')}
                withAsterisk
                value={draft.summary}
                onChange={(event) =>
                  setDraft((d) => ({ ...d, summary: event.currentTarget.value }))
                }
                autosize
                minRows={3}
                maxLength={DAILY_SUMMARY_MAX}
                error={missing('summary')}
                description={k('counter', { count: draft.summary.length, max: DAILY_SUMMARY_MAX })}
                inputWrapperOrder={['label', 'input', 'description', 'error']}
              />
              {draft.kind === 'defect_observation' || draft.kind === 'safety' ? (
                <p className={classes.hint}>{k('hseHint')}</p>
              ) : null}
            </>
          ) : (
            <Textarea
              label={k(tab)}
              value={typeof draft.data[tab] === 'string' ? String(draft.data[tab]) : ''}
              onChange={(event) => setData(tab, event.currentTarget.value)}
              autosize
              minRows={8}
              maxLength={2000}
            />
          )}
        </div>
        <div className={classes.entryFiles}>
          <h3 className={classes.subTitle}>{k('attachments')}</h3>
          {detail?.can.attach ? (
            <FileButton
              accept="image/jpeg,image/png,application/pdf"
              onChange={(file) => {
                if (!file) return;
                setBusy('upload');
                setError(null);
                editor
                  .upload(detail.id, file)
                  .then(() => onSaved(detail.id))
                  .catch((failure: unknown) => setError(errorText(failure)))
                  .finally(() => setBusy(null));
              }}
            >
              {(props) => (
                <UnstyledButton {...props} className={classes.drop} disabled={busy !== null}>
                  <IconCloudUpload size={26} aria-hidden />
                  <span>{busy === 'upload' ? k('uploading') : k('upload')}</span>
                  <small>{k('uploadFormats')}</small>
                </UnstyledButton>
              )}
            </FileButton>
          ) : (
            <p className={classes.muted}>{k('attachHint')}</p>
          )}
          {detail && detail.files.length > 0 ? (
            <ul className={classes.files}>
              {detail.files.map((f) => (
                <li key={f.id}>
                  <IconFile size={16} aria-hidden />
                  <span className={classes.ellipsis}>{f.name}</span>
                  <small>{Math.max(1, Math.round(f.size / 1024))} KB</small>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        {error ? (
          <p className={classes.error} role="alert">
            {error}
          </p>
        ) : null}
        <div className={classes.entryActions}>
          <Button
            type="submit"
            leftSection={<IconDeviceFloppy size={16} />}
            loading={busy === 'save'}
          >
            {k('save')}
          </Button>
          <Button
            variant="light"
            leftSection={<IconSend size={16} />}
            loading={busy === 'submit'}
            onClick={() => act('submit')}
          >
            {t('daily.detail.submit')}
          </Button>
          <Button
            variant="default"
            leftSection={<IconRefresh size={16} />}
            onClick={() => {
              setDraft(initial);
              setInvalid([]);
              setError(null);
            }}
          >
            {k('reset')}
          </Button>
          <Button variant="subtle" onClick={onCancel}>
            {k('cancel')}
          </Button>
        </div>
      </form>
    </section>
  );
}
